import { useRef, useEffect, useCallback, useState } from "react";
import { Message } from "@/types";

// ===== Constants =====
const SCROLL_CONFIG = {
  // Bu kadar yakınsa kullanıcı yeniden "altta" sayılır ve takip devam eder.
  // Liste büyürken ara kareler eşiğin üstünde kalabildiği için kasıtlı olarak
  // dar: yukarı kaydıran kişi bir sonraki chunk'ta aşağı çekilmesin.
  NEAR_BOTTOM_THRESHOLD: 48,
  // Scroll to bottom butonunun göründüğü threshold (px)
  SCROLL_BUTTON_THRESHOLD: 100,
  // Yeni mesaj için auto-scroll threshold (ms)
  RECENT_MESSAGE_THRESHOLD: 10000, // 10 saniye
  // Infinite scroll trigger distance from top (px)
  INFINITE_SCROLL_TRIGGER: 200,
} as const;

interface UseChatScrollOptions {
  // Mesajlar
  messages: Message[];
  // Yeni mesaj gönderildi mi?
  pendingUserMessage?: string | null;
  // Chat key (izolasyon için - chat değişince internal refs resetlenir)
  chatKey?: string | null;
  // Pagination state
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => Promise<any>;
  // Loading indicator state setter
  setShowLoadingIndicator?: (show: boolean) => void;
}

interface UseChatScrollReturn {
  // Container ref (scrollbar gibi .current okuyanlar için)
  containerRef: React.RefObject<HTMLDivElement | null>;
  // Scroller div'ine verilir; node geç mount olduğunda dinleyiciler bağlanır
  attachContainer: (node: HTMLDivElement | null) => void;
  // Padding dahil tüm listenin kutusu. Yükseklik takibi bunun border-box'ında.
  attachContent: (node: HTMLDivElement | null) => void;
  // Bottom ref (spacer için)
  bottomRef: React.RefObject<HTMLDivElement | null>;
  // Scroll to bottom function
  scrollToBottom: (smooth?: boolean) => void;
  // Scroll to bottom button visibility
  showScrollToBottom: boolean;
}

/**
 * Smart chat scroll hook
 *
 * Features:
 * - Auto-scroll on new messages (only if user is near bottom)
 * - Scroll position restore on pagination
 * - Scroll to bottom button visibility
 * - Infinite scroll trigger
 * - Smart scroll detection (only scroll for recent messages)
 */
export function useChatScroll({
  messages,
  pendingUserMessage,
  chatKey,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  setShowLoadingIndicator,
}: UseChatScrollOptions): UseChatScrollReturn {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Node'lar geç mount olur (loading/empty erken return). State, observer'ın
  // o anda bağlanmasını sağlar; ref ise scrollbar'ın .current okumasını.
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);

  // Scroll tracking refs
  // "Altta takip et" niyeti. Konumdan değil, kullanıcının jestinden düşer:
  // liste büyüyünce scrollTop yerinde kalır ve mesafe açılır; bunu "yukarı
  // çıktı" sanıp takibi kesmek, düşünme satırı ve composer padding'i
  // geldikten sonra dibin kısa kalmasına yol açıyordu.
  const pinnedRef = useRef<boolean>(true);
  const prevScrollTopRef = useRef(0);
  const prevScrollHeightRef = useRef(0);
  const touchStartYRef = useRef<number | null>(null);
  const prevMessageCountRef = useRef(messages.length);
  const scrollPositionBeforePaginationRef = useRef<number>(0);
  const scrollHeightBeforePaginationRef = useRef<number>(0);
  const isPaginationLoadingRef = useRef<boolean>(false);

  // Infinite scroll refs
  const lastRequestTimeRef = useRef<number>(0);
  const requestInProgressRef = useRef<boolean>(false);

  // UI state
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const attachContainer = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    setContainerEl(node);
  }, []);

  const attachContent = useCallback((node: HTMLDivElement | null) => {
    setContentEl(node);
  }, []);

  // Reset internal refs when chat changes (prevents cross-chat scroll bleed)
  useEffect(() => {
    prevMessageCountRef.current = messages.length;
    isPaginationLoadingRef.current = false;
    pinnedRef.current = true;
    requestInProgressRef.current = false;
    lastRequestTimeRef.current = 0;
    setShowScrollToBottom(false);
    const container = containerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      prevScrollTopRef.current = container.scrollTop;
      prevScrollHeightRef.current = container.scrollHeight;
    }
  }, [chatKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Scroll to bottom function (ChatGPT-style) =====
  const scrollToBottom = useCallback((smooth = true) => {
    const container = containerRef.current;
    if (!container) return;

    pinnedRef.current = true;
    setShowScrollToBottom(false);

    if (smooth) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
      return;
    }

    // Anlık atama: süren bir smooth scroll'u keser ve stream sırasında
    // saniyede birçok kez güvenle çağrılabilir.
    container.scrollTop = container.scrollHeight;
    prevScrollTopRef.current = container.scrollTop;
    prevScrollHeightRef.current = container.scrollHeight;
  }, []);

  // ===== Pagination Scroll Position Restore (ChatGPT-style) =====
  // Eski mesajlar yukarıdan prepend edildiğinde scroll pozisyonunu koru
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (isFetchingNextPage && !isPaginationLoadingRef.current) {
      // Pagination başladığında scroll pozisyonunu ve scrollHeight'i kaydet
      scrollPositionBeforePaginationRef.current = container.scrollTop;
      scrollHeightBeforePaginationRef.current = container.scrollHeight;
      isPaginationLoadingRef.current = true;
    } else if (
      !isFetchingNextPage &&
      isPaginationLoadingRef.current &&
      container
    ) {
      // Pagination bitti, scroll pozisyonunu geri yükle
      // ChatGPT-style: Yeni içerik üstten eklendiğinde scrollHeight artar
      // Kullanıcının görüntülediği pozisyonu korumak için scrollTop'u artırmalıyız
      requestAnimationFrame(() => {
        if (container) {
          const oldScrollHeight = scrollHeightBeforePaginationRef.current;
          const newScrollHeight = container.scrollHeight;
          const scrollHeightDelta = newScrollHeight - oldScrollHeight;

          // Yeni scrollTop = eski scrollTop + scrollHeight artışı
          // Böylece kullanıcı aynı içeriği görmeye devam eder
          const newScrollTop =
            scrollPositionBeforePaginationRef.current + scrollHeightDelta;

          // Scroll pozisyonunu restore et (instant, no smooth)
          container.scrollTop = newScrollTop;
        }
        isPaginationLoadingRef.current = false;
      });
    }
  }, [isFetchingNextPage]);

  // ===== Pin on send =====
  // Göndermek, nerede olursa olsun yeniden dibe kilitler. Asıl takip
  // aşağıdaki ResizeObserver'da: düşünme satırı, stream metni, görsel ve
  // composer padding'i DOM'a bu effect'ten sonra girer.
  useEffect(() => {
    if (!pendingUserMessage) return;
    scrollToBottom(false);
  }, [pendingUserMessage, scrollToBottom]);

  // Yeni ve gerçekten taze bir satır, kullanıcı zaten dibe kilitliyse
  // kilidi açık tutar. Yükseklik artışı observer'dan takip edilir; burada
  // scroll etmek DOM büyümeden önce koştuğu için kısa kalıyordu.
  useEffect(() => {
    const currentMessageCount = messages.length;
    const prevMessageCount = prevMessageCountRef.current;
    prevMessageCountRef.current = currentMessageCount;

    if (isPaginationLoadingRef.current) return;
    if (currentMessageCount <= prevMessageCount || !pinnedRef.current) return;

    const lastMessage = messages[messages.length - 1];
    const isRecentMessage =
      lastMessage &&
      new Date().getTime() - new Date(lastMessage.timestamp).getTime() <
        SCROLL_CONFIG.RECENT_MESSAGE_THRESHOLD;

    if (lastMessage && isRecentMessage) {
      scrollToBottom(false);
    }
  }, [messages, scrollToBottom]);

  // ===== Stick to bottom =====
  // Tetik, mesaj sayısı değil kutunun yüksekliği. Effect DOM'dan önce
  // koşuyordu; "Düşünüyorum…" satırı ve alttaki composer payı ondan sonra
  // gelince scrollTop gerçek dibin kısa kalıyordu.
  useEffect(() => {
    if (!containerEl || !contentEl) return;

    prevScrollTopRef.current = containerEl.scrollTop;
    prevScrollHeightRef.current = containerEl.scrollHeight;

    const follow = () => {
      if (!pinnedRef.current || isPaginationLoadingRef.current) return;
      containerEl.scrollTop = containerEl.scrollHeight;
      prevScrollTopRef.current = containerEl.scrollTop;
      prevScrollHeightRef.current = containerEl.scrollHeight;
    };

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(follow);
      // border-box: alttaki composer payı (paddingBottom) da bu kutuda.
      observer.observe(contentEl, { box: "border-box" });
      observer.observe(containerEl, { box: "border-box" });
    } else {
      follow();
    }

    return () => observer?.disconnect();
  }, [containerEl, contentEl]);

  // ===== Infinite scroll + kullanıcı niyeti =====
  useEffect(() => {
    if (!containerEl || !fetchNextPage) return;

    const scrollable = () =>
      containerEl.scrollHeight - containerEl.clientHeight > 1;

    const leave = () => {
      if (!scrollable()) return;
      pinnedRef.current = false;
    };

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = containerEl;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const prevTop = prevScrollTopRef.current;
      const prevHeight = prevScrollHeightRef.current;
      prevScrollTopRef.current = scrollTop;
      prevScrollHeightRef.current = scrollHeight;

      const atBottom = distanceFromBottom <= SCROLL_CONFIG.NEAR_BOTTOM_THRESHOLD;
      if (atBottom) {
        pinnedRef.current = true;
      } else if (scrollTop < prevTop && scrollHeight >= prevHeight) {
        // Yalnızca kullanıcı üst kenarı yukarı taşır. Büyüme scrollTop'u
        // yerinde bırakır, bizim scroll'umuz yükseltir. Küçülme de
        // clamp ile düşürebilir; o kasıt değildir.
        pinnedRef.current = false;
      }

      setShowScrollToBottom(
        distanceFromBottom > SCROLL_CONFIG.SCROLL_BUTTON_THRESHOLD
      );

      const isNearTop = scrollTop < SCROLL_CONFIG.INFINITE_SCROLL_TRIGGER;
      const shouldShowLoading =
        scrollTop < SCROLL_CONFIG.INFINITE_SCROLL_TRIGGER * 1.5;

      setShowLoadingIndicator?.(
        shouldShowLoading && hasNextPage && !isFetchingNextPage
      );

      if (isNearTop && hasNextPage && !isFetchingNextPage) {
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestTimeRef.current;

        if (timeSinceLastRequest > 500 && !requestInProgressRef.current) {
          requestInProgressRef.current = true;
          lastRequestTimeRef.current = now;

          fetchNextPage().finally(() => {
            requestInProgressRef.current = false;
          });
        }
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) leave();
    };

    const onTouchStart = (event: TouchEvent) => {
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event: TouchEvent) => {
      const start = touchStartYRef.current;
      const now = event.touches[0]?.clientY;
      if (start === null || now === undefined) return;
      if (now - start > 8) {
        leave();
        touchStartYRef.current = null;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "ArrowUp" ||
        event.key === "PageUp" ||
        event.key === "Home"
      ) {
        leave();
      }
    };

    containerEl.addEventListener("scroll", handleScroll, { passive: true });
    containerEl.addEventListener("wheel", onWheel, { passive: true });
    containerEl.addEventListener("touchstart", onTouchStart, { passive: true });
    containerEl.addEventListener("touchmove", onTouchMove, { passive: true });
    containerEl.addEventListener("keydown", onKeyDown);

    return () => {
      containerEl.removeEventListener("scroll", handleScroll);
      containerEl.removeEventListener("wheel", onWheel);
      containerEl.removeEventListener("touchstart", onTouchStart);
      containerEl.removeEventListener("touchmove", onTouchMove);
      containerEl.removeEventListener("keydown", onKeyDown);
    };
  }, [
    containerEl,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    setShowLoadingIndicator,
  ]);

  return {
    containerRef,
    attachContainer,
    attachContent,
    bottomRef,
    scrollToBottom,
    showScrollToBottom,
  };
}
